import { Component, Prop, State, Element, Event, EventEmitter, Watch, h, Host } from '@stencil/core';

export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

export type WidgetPosition = 'bottom-right' | 'bottom-left';

export interface ChatWidgetConfig {
  // Agent Information
  agentName?: string;
  agentRole?: string;
  agentAvatar?: string;
  
  // Appearance
  primaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  width?: number;
  height?: number;
  
  // Messages
  welcomeMessage?: string;
  thankYouMessage?: string;
  placeholderText?: string;
  
  // Features
  showMinimizeButton?: boolean;
  enableFileUpload?: boolean;
  enableSpeech?: boolean;
  showTimestamps?: boolean;
  collapsed?: boolean;
  
  // Position
  position?: WidgetPosition;
  
  // Branding
  showPoweredBy?: boolean;
  poweredByLogo?: string;
  poweredByText?: string;
  poweredByBrand?: string;
  poweredByUrl?: string;
  poweredByColor?: string;
  
  // API
  agentId?: string;
  apikey?: string;
  apiEndpoint?: string;
  enableDebug?: string;
  tokenEndpoint?: string;
  clientId?: string;
  clientSecret?: string;
  
  // Advanced
  zIndex?: number;
  customCSS?: string;
  
  // Animation and Popup
  enablePulsingAnimation?: boolean;
  popupMessage?: string;
  popupShowDelay?: number;
  popupHideDelay?: number;

  // Customer Metadata
  customerMetadata?: Record<string, any>;
}

@Component({
  tag: 'nerd-chat-widget',
  styleUrl: 'chat-widget.scss',
  shadow: true,
})
export class ChatWidget {
  @Element() el: HTMLElement;

  // Configuration props
  @Prop() config: ChatWidgetConfig = {};
  @Prop() agentName: string = 'Support Agent';
  @Prop() agentRole: string = 'Customer Support';
  @Prop() agentAvatar: string = '';
  @Prop() primaryColor: string = '#2d3e50';
  @Prop() accentColor: string = '#4e8cff';
  @Prop() welcomeMessage: string = 'Hi! How can I help you today?';
  @Prop() position: WidgetPosition = 'bottom-right';
  @Prop() showMinimizeButton: boolean = true;
  @Prop() enableFileUpload: boolean = false;
  @Prop() enableSpeech: boolean = false;
  @Prop() showTimestamps: boolean = true;
  @Prop() agentId: string = '';
  @Prop() apikey: string = '';
  @Prop() token: string = '';
  @Prop() apiEndpoint: string = '';
  @Prop() enableDebug: string = '';
  @Prop() placeholderText: string = 'Type your message...';
  @Prop() thankYouMessage: string = 'Thank you for chatting with us!';
  @Prop() showPoweredBy: boolean = true;
  @Prop() poweredByLogo: string = 'https://innovationincubator.com/wp-content/uploads/2023/05/Innovation-Incubator-logo.png';
  @Prop() poweredByText: string = 'Powered by';
  @Prop() poweredByBrand: string = 'NerdAgent';
  @Prop() poweredByUrl: string = 'https://nerdagent.com';
  @Prop() poweredByColor: string = '#4e8cff';
  @Prop() width: number = 350;
  @Prop() height: number = 500;
  @Prop() fontFamily: string = 'Segoe UI, Arial, sans-serif';
  @Prop() zIndex: number = 10000;
  @Prop() customCSS: string = '';
  @Prop() collapsedByDefault: boolean = false;
  @Prop() enablePulsingAnimation: boolean = true;
  @Prop() popupMessage: string = 'Hi, How can I help you?';
  @Prop() popupShowDelay: number = 2;
  @Prop() popupHideDelay: number = 5;
  @Prop() customerMetadata: Record<string, any> = {};

  // State
  @State() messages: ChatMessage[] = [];
  @State() isMinimized: boolean = false; // Start open by default
  @State() isTyping: boolean = false;
  @State() inputValue: string = '';
  @State() isChatOpen: boolean = true;
  @State() currentPosition: WidgetPosition = 'bottom-right';
  @State() showPopup: boolean = false;
  @State() isUploading: boolean = false;

  // Events
  @Event() messageSent: EventEmitter<{ message: string; widget: HTMLElement }>;
  @Event() widgetOpened: EventEmitter<{ widget: HTMLElement }>;
  @Event() widgetClosed: EventEmitter<{ widget: HTMLElement }>;

  private messageIdCounter = 0;
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null;

  @Watch('apikey')
  async handleApikeyChange(newValue: string, oldValue: string) {
    console.log('[Chat Widget] API key changed:', { oldValue, newValue });
    if (newValue && newValue !== oldValue) {
      await this.loadAgentConfiguration();
    }
  }

  async componentDidLoad() {
    this.currentPosition = this.position;

    console.log('[Chat Widget] componentDidLoad - API Key:', this.apikey);
    console.log('[Chat Widget] componentDidLoad - Token:', this.token ? 'Present' : 'Not provided');
    console.log('[Chat Widget] componentDidLoad - Agent ID:', this.agentId);
    
    if (this.apikey || this.token) {
      await this.loadAgentConfiguration();
    }
    
    const config = this.getConfig();
    this.isMinimized = config.collapsed || this.collapsedByDefault;
    
    this.loadFontAwesome();
    
    this.applyCustomCSS();
    
    if (this.isMinimized) {
      setTimeout(() => {
        this.showPopupMessage();
      }, 100);
    } else {
        if (config.welcomeMessage) {
          setTimeout(() => {
            this.addMessage(config.welcomeMessage, false);
          }, 500);
        }
        
        setTimeout(() => this.scrollToBottom(), 600);
    }
    
  }

  @Watch('config')
  configChanged() {
  }

  @Watch('customCSS')
  customCSSChanged() {
    this.applyCustomCSS();
  }


  @Watch('position')
  positionChanged() {
    this.currentPosition = this.position;
  }

  @Watch('collapsedByDefault')
  collapsedByDefaultChanged() {
    this.isMinimized = !this.collapsedByDefault;
    
    if (this.isMinimized) {
      setTimeout(() => {
        this.showPopupMessage();
      }, 100);
    } else {
      this.showPopup = false;
      if (this.messages.length === 0) {
        const config = this.getConfig();
        if (config.welcomeMessage) {
          setTimeout(() => {
            this.addMessage(config.welcomeMessage, false);
          }, 500);
        }
      }
    }
  }

  private getConfig(): ChatWidgetConfig {
    const mergedConfig = {
      agentName: this.agentName,
      agentRole: this.agentRole,
      agentAvatar: this.agentAvatar,
      primaryColor: this.primaryColor,
      accentColor: this.accentColor,
      welcomeMessage: this.welcomeMessage,
      position: this.position,
      showMinimizeButton: this.showMinimizeButton,
      enableFileUpload: this.enableFileUpload,
      enableSpeech: this.enableSpeech,
      showTimestamps: this.showTimestamps,
      agentId: this.agentId,
      apikey: this.apikey,
      apiEndpoint: this.apiEndpoint,
      enableDebug: this.enableDebug,
      placeholderText: this.placeholderText,
      thankYouMessage: this.thankYouMessage,
      showPoweredBy: this.showPoweredBy,
      poweredByLogo: this.poweredByLogo,
      poweredByText: this.poweredByText,
      poweredByBrand: this.poweredByBrand,
      poweredByUrl: this.poweredByUrl,
      poweredByColor: this.poweredByColor,
      width: this.width,
      height: this.height,
      fontFamily: this.fontFamily,
      zIndex: this.zIndex,
      customCSS: this.customCSS,
      collapsed: this.collapsedByDefault,
      enablePulsingAnimation: this.enablePulsingAnimation,
      popupMessage: this.popupMessage,
      popupShowDelay: this.popupShowDelay,
      popupHideDelay: this.popupHideDelay,
      customerMetadata: this.customerMetadata,
      ...this.config
    };
    
    return mergedConfig;
  }

  private loadFontAwesome() {
    if (!document.getElementById('fa-cdn-stencil')) {
      const link = document.createElement('link');
      link.id = 'fa-cdn-stencil';
      link.rel = 'stylesheet';
      link.href = 'https://use.fontawesome.com/releases/v6.4.2/css/all.css';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }
  }

  private applyCustomCSS() {
    if (this.customCSS) {
      const existingStyle = document.getElementById('nerd-chat-widget-custom-css');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      const style = document.createElement('style');
      style.id = 'nerd-chat-widget-custom-css';
      style.textContent = this.customCSS;
      document.head.appendChild(style);
    }
  }

  private addMessage(text: string, isUser: boolean = false) {
    const message: ChatMessage = {
      id: `msg-${++this.messageIdCounter}`,
      text,
      isUser,
      timestamp: new Date()
    };
    
    this.messages = [...this.messages, message];
    
    setTimeout(() => this.scrollToBottom(), 100);
  }

  private async sendMessage() {
    const text = this.inputValue.trim();
    if (!text) return;

    this.addMessage(text, true);
    this.inputValue = '';

    this.messageSent.emit({ message: text, widget: this.el });

    if (this.agentId) {
      await this.sendMessageToAgent(text);
    } 
  }



  private getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'accept': 'application/json',
      'Origin': window.location.origin
    };
    
    // Prefer token (JWT) over apikey
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
      console.log('[Chat Widget] Using JWT token for authentication');
    } else if (this.apikey) {
      headers['agent-key'] = this.apikey;
      console.log('[Chat Widget] Using API key for authentication');
    }
    
    return headers;
  }

  private getApiUrlFromApikey(token: string): string {
    if (!token || token.length < 3) {
      const envApiUrl = (window as any).ENV_API_URL;
      if (envApiUrl) {
        return envApiUrl;
      }
      const hostname = window.location.hostname.toLowerCase();
      if (hostname.includes('dev') || hostname.includes('localhost')) {
        return 'https://agent-api.dev.nerdagent.ai';
      } else if (hostname.includes('sandbox')) {
        return 'https://agent-api.sandbox.nerdagent.ai';
      } else if (hostname.includes('uat') || hostname.includes('staging')) {
        return 'https://agent-api.uat.nerdagent.ai';
      } else if (hostname.includes('beta')) {
        return 'https://agent-api.beta.nerdagent.ai';
      } else {
        return 'https://agent-api.nerdagent.ai';
      }
    }

    // Check if token is an API key (starts with known prefixes)
    const prefix = token.substring(0, 3);
    const isApiKey = prefix === 'DT_' || prefix === 'ST_' || prefix === 'UT_' || prefix === 'BT_' || prefix === 'PN_';

    if (isApiKey) {
      // Use prefix logic for API keys
      switch (prefix) {
        case 'DT_':
          return 'https://agent-api.dev.nerdagent.ai';
        case 'ST_':
          return 'https://agent-api.sandbox.nerdagent.ai';
        case 'UT_':
          return 'https://agent-api.uat.nerdagent.ai';
        case 'BT_':
          return 'https://agent-api.beta.nerdagent.ai';
        case 'PN_':
          return 'https://agent-api.nerdagent.ai';
        default:
          return 'https://agent-na-dev.api.iinerds.com';
      }
    }
  }


  
  private async sendMessageToAgent(message: string) {
    this.isTyping = true;

    try {
      
      if (!this.apikey && !this.token) {
        throw new Error('No API key or token provided');
      }
      
      const baseApiUrl = this.getApiUrlFromApikey(this.apikey || this.token);
      const apiUrl = `${baseApiUrl}/api/v1/agent/${this.agentId}/invoke-smart/stream?show_execution_time=false`;
      
      const formData = new FormData();
      const body: Record<string, any> = {
        service_provider: "strandsagent",
        prompt: message,
        session_id: this.getSessionId(),
        response_format: "text"
      };

      const metadata = this.getConfig().customerMetadata;
      if (metadata && Object.keys(metadata).length > 0) {
        body.customer_metadata = metadata;
      }
      
      formData.append('body', JSON.stringify(body));

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let streamedText = '';
      
      const streamMessageId = `msg-${++this.messageIdCounter}`;
      const streamMessage: ChatMessage = {
        id: streamMessageId,
        text: '',
        isUser: false,
        timestamp: new Date()
      };
      this.messages = [...this.messages, streamMessage];

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          streamedText += chunk;
          
          this.messages = this.messages.map(msg => 
            msg.id === streamMessageId 
              ? { ...msg, text: streamedText }
              : msg
          );
          
          this.debouncedScrollToBottom();
        }
      } finally {
        reader.releaseLock();
      }

      this.isTyping = false;

      if (!streamedText.trim()) {
        this.messages = this.messages.map(msg => 
          msg.id === streamMessageId 
            ? { ...msg, text: 'No response received from the agent.' }
            : msg
        );
      }

    } catch (error) {
      console.error('API Error:', error);
      this.isTyping = false;
      this.addMessage('Sorry, I encountered an error. Please try again.', false);
    }
  }

  private toggleMinimize() {
    this.isMinimized = !this.isMinimized;
    
    if (this.isMinimized) {
      setTimeout(() => {
        this.showPopupMessage();
      }, 100);
      this.widgetClosed.emit({ widget: this.el });
    } else {
      this.showPopup = false;
      this.widgetOpened.emit({ widget: this.el });
    }
  }

  private closeWidget() {
    this.isMinimized = true;
    this.clearConversation();
    setTimeout(() => {
      this.showPopupMessage();
    }, 100);
    this.widgetClosed.emit({ widget: this.el });
  }

  private openWidget() {
    this.isMinimized = false;
    this.showPopup = false;
    
        if (this.messages.length === 0) {
          const config = this.getConfig();
          if (config.welcomeMessage) {
            setTimeout(() => {
              this.addMessage(config.welcomeMessage, false);
            }, 500);
          }
        }
        
        setTimeout(() => this.scrollToBottom(), 100);
    
    this.widgetOpened.emit({ widget: this.el });
  }

  private clearConversation() {
    this.messages = [];
    this.inputValue = '';
    this.isTyping = false;
  }

  private showPopupMessage() {
    const config = this.getConfig();
    
    setTimeout(() => {
      this.showPopup = true;
      
      setTimeout(() => {
        this.showPopup = false;
      }, config.popupHideDelay * 1000);
    }, config.popupShowDelay * 1000);
  }

  private handleKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.sendMessage();
    }
  }

  private handleInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.inputValue = target.value;
  }

  private handleFileUpload(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      this.addMessage('File size too large. Please upload files smaller than 10MB.', false);
      return;
    }
    
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain', 'text/csv'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      this.addMessage('File type not supported. Please upload images, PDFs, documents, or Excel files.', false);
      return;
    }
    
    this.uploadFile(file);
  }

  private async uploadFile(file: File) {
    this.isUploading = true;
    const fileMessageId = `msg-${++this.messageIdCounter}`;
    
    try {
      const fileMessage: ChatMessage = {
        id: fileMessageId,
        text: `📎 Uploading ${file.name}...`,
        isUser: true,
        timestamp: new Date()
      };
      this.messages = [...this.messages, fileMessage];
      
      await this.sendFileToAgent(file);
      
      this.messages = this.messages.map(msg => 
        msg.id === fileMessageId 
          ? { ...msg, text: `📎 ${file.name} uploaded successfully` }
          : msg
      );
      
    } catch (error) {
      console.error('File upload error:', error);
      this.messages = this.messages.map(msg => 
        msg.id === fileMessageId 
          ? { ...msg, text: 'Failed to upload ' + file.name }
          : msg
      );
      this.addMessage('Failed to upload file. Please try again.', false);
    } finally {
      this.isUploading = false;
    }
  }

  private async sendFileToAgent(file: File) {
    this.isTyping = true;

    try {
      if (!this.apikey && !this.token) {
        throw new Error('No API key or token provided');
      }
      
      const baseApiUrl = this.getApiUrlFromApikey(this.apikey || this.token);
      const apiUrl = `${baseApiUrl}/api/v1/agent/${this.agentId}/invoke`;
      
      const formData = new FormData();
      const body: Record<string, any> = {
        service_provider: "strandsagent",
        prompt: `User uploaded file: ${file.name}`,
        session_id: this.getSessionId(),
        response_format: "text"
      };

      const metadata = this.getConfig().customerMetadata;
      if (metadata && Object.keys(metadata).length > 0) {
        body.customer_metadata = metadata;
      }
      
      formData.append('body', JSON.stringify(body));
      
      formData.append('file', file, file.name);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      
      this.isTyping = false;

      if (responseData.status_code === 200 && responseData.data && responseData.data.response) {
        const responseText = responseData.data.response;
        
        const s3UrlMatch = responseText.match(/https:\/\/[^\s]+\.(jpeg|jpg|png|gif|webp|pdf|doc|docx|xls|xlsx)/i);
        const s3Url = s3UrlMatch ? s3UrlMatch[0] : null;
        
        console.log('S3 URL found:', s3Url);
        console.log('Response text:', responseText);
        
        let responseMessage = responseText;
        if (s3Url && /\.(jpeg|jpg|png|gif|webp)$/i.test(s3Url)) {
          responseMessage = `${responseText}\n\n📷 **Image Preview:**\n![Uploaded Image](${s3Url})\n\n🔗 **Direct Link:** ${s3Url}`;
        }
        
        this.addMessage(responseMessage, false);
      } else {
        this.addMessage('File uploaded successfully, but no response received from the agent.', false);
      }

    } catch (error) {
      console.error('File API Error:', error);
      this.isTyping = false;
      this.addMessage('Sorry, I encountered an error processing your file. Please try again.', false);
      throw error;
    }
  }

  private getSessionId(): string {
    return 'session-' + Math.random().toString(36).substring(2, 11);
  }

  private async loadAgentConfiguration(): Promise<void> {
    try {
      if ((!this.apikey && !this.token) || !this.agentId) {
        console.log('[Chat Widget] API key/token or agent ID not provided, skipping configuration load');
        console.log('[Chat Widget] API Key:', this.apikey);
        console.log('[Chat Widget] Token:', this.token ? 'Present' : 'Not provided');
        console.log('[Chat Widget] Agent ID:', this.agentId);
        return;
      }

      const baseApiUrl = this.getApiUrlFromApikey(this.apikey || this.token);

      const configUrl = `${baseApiUrl}/api/v1/chatbuilder/?agent_id=${this.agentId}`;
      
      console.log('[Chat Widget] Loading configuration from:', configUrl);
      console.log('[Chat Widget] Using auth:', this.token ? 'JWT Token' : 'API Key');
      
      const response = await fetch(configUrl, {
        headers: this.getAuthHeaders()
      });

      console.log('[Chat Widget] Config API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Chat Widget] Config API error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('[Chat Widget] Config API result:', result);
      const apiConfig = result.configuration || {};
      
      if (apiConfig.agentName) this.agentName = apiConfig.agentName;
      if (apiConfig.agentRole) this.agentRole = apiConfig.agentRole;
      if (apiConfig.agentAvatar) this.agentAvatar = apiConfig.agentAvatar;
      if (apiConfig.primaryColor) this.primaryColor = apiConfig.primaryColor;
      if (apiConfig.accentColor) this.accentColor = apiConfig.accentColor;
      if (apiConfig.welcomeMessage) this.welcomeMessage = apiConfig.welcomeMessage;
      if (apiConfig.placeholderText) this.placeholderText = apiConfig.placeholderText;
      if (apiConfig.showMinimizeButton !== undefined) this.showMinimizeButton = apiConfig.showMinimizeButton;
      if (apiConfig.enableFileUpload !== undefined) this.enableFileUpload = apiConfig.enableFileUpload;
      if (apiConfig.enableSpeech !== undefined) this.enableSpeech = apiConfig.enableSpeech;
      if (apiConfig.showTimestamps !== undefined) this.showTimestamps = apiConfig.showTimestamps;
      if (apiConfig.showPoweredBy !== undefined) this.showPoweredBy = apiConfig.showPoweredBy;
      if (apiConfig.width) this.width = apiConfig.width;
      if (apiConfig.height) this.height = apiConfig.height;
      if (apiConfig.position) this.position = apiConfig.position;
      
      console.log('[Chat Widget] Configuration loaded successfully from API');
      
    } catch (error) {
      console.error('[Chat Widget] Error loading configuration from API:', error);
    }
  }

  private getAgentAvatarUrl(): string {
    const config = this.getConfig();
    
    if (config.agentAvatar && config.agentAvatar.trim() !== '') {
      return config.agentAvatar;
    }
    
    const agentName = config.agentName || 'Support Agent';
    const encodedName = encodeURIComponent(agentName);
    return `https://ui-avatars.com/api/?name=${encodedName}&background=2d3e50&color=ffffff&size=44`;
  }

  private formatTime(date: Date): string {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private formatMessageText(text: string): string {
    if (!text) return '';
    
    let formattedText = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt, url) => {
      return `<div style="margin: 8px 0;">
                <img src="${url}" alt="${alt}" style="max-width: 100%; height: auto; border-radius: 8px; display: block;"/>
              </div>`;
    });
    
    formattedText = formattedText.replace(/\n/g, '<br>');
    
   formattedText = formattedText.replace(/(?<!src=")(https?:\/\/[^\s<>"]+)(?![^<]*>)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: var(--accent-color); text-decoration: underline;">$1</a>');
    
    return formattedText;
  }

  private scrollToBottom() {
    const messagesContainer = this.el.shadowRoot?.querySelector('.messages-container') as HTMLElement;
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  private debouncedScrollToBottom() {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
      
    this.scrollTimeout = setTimeout(() => {
      this.scrollToBottom();
      this.scrollTimeout = null;
    }, 100);
  }


  render() {
    const config = this.getConfig();
    
    
    const getPositionStyles = () => {
      switch (this.currentPosition) {
        case 'bottom-left':
          return { bottom: '20px', left: '20px', top: 'auto', right: 'auto' };
        case 'bottom-right':
        default:
          return { bottom: '20px', right: '20px', top: 'auto', left: 'auto' };
      }
    };
    
    const widgetClasses = {
      'chat-widget': true,
      'minimized': this.isMinimized
    };

    return (
      <Host>
        {/* Minimize Icon (shown when minimized) */}
        {this.isMinimized && (
          <div 
            class={`minimize-icon ${config.enablePulsingAnimation ? 'pulsing' : ''}`}
            onClick={() => this.openWidget()}
            style={{
              '--primary-color': config.primaryColor || '#2d3e50',
              '--font-family': config.fontFamily || 'Segoe UI, Arial, sans-serif',
              'z-index': (config.zIndex || 10000).toString(),
              ...getPositionStyles()
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-1h12v1zm0-3H6V9h12v1zm0-3H6V6h12v1z"/>
            </svg>
            
            {/* Popup Message */}
            {this.showPopup && (
              <div class="popup-message">
                {config.popupMessage}
              </div>
            )}
          </div>
        )}

        {/* Main Widget (shown when not minimized) */}
        {!this.isMinimized && (
          <div 
            class={widgetClasses}
            style={{
              '--primary-color': config.primaryColor || '#2d3e50',
              '--background-color': '#ffffff',
              '--accent-color': config.accentColor || '#4e8cff',
              '--widget-width': `${config.width || 350}px`,
              '--widget-height': `${config.height || 500}px`,
              '--font-family': config.fontFamily || 'Segoe UI, Arial, sans-serif',
              'z-index': (config.zIndex || 10000).toString(),
              ...getPositionStyles()
            }}
          >
        {/* Header */}
        <div class="chat-header">
          <div class="agent-info">
            <img src={this.getAgentAvatarUrl()} alt="Agent" class="agent-avatar" />
            <div class="agent-details">
              <h4>{config.agentName}</h4>
              <p>{config.agentRole}</p>
            </div>
          </div>
          <div class="controls">
            {config.showMinimizeButton && (
              <button 
                class="control-btn minimize-btn" 
                onClick={() => this.toggleMinimize()}
                title={this.isMinimized ? 'Maximize' : 'Minimize'}
              >
                {this.isMinimized ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 14l5-5 5 5z"/>
                  </svg>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 10l5 5 5-5z"/>
                  </svg>
                )}
              </button>
            )}
            <button 
              class="control-btn close-btn" 
              onClick={() => this.closeWidget()}
              title="Close"
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div class="messages-container">
          {this.messages.map(message => (
            <div key={message.id} class={`message ${message.isUser ? 'user' : 'agent'}`}>
              {message.isUser ? (
                <div class="message-avatar user-avatar-icon">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
              ) : (
                <img 
                  src={this.getAgentAvatarUrl()} 
                  alt="Agent" 
                  class="message-avatar" 
                />
              )}
              <div class="message-content">
                <div class="message-text" innerHTML={this.formatMessageText(message.text)}></div>
                {config.showTimestamps && (
                  <div class="message-time">{this.formatTime(message.timestamp)}</div>
                )}
              </div>
            </div>
          ))}
          
          {this.isTyping && (
            <div class="typing-indicator">
              <span>{config.agentName} is typing</span>
              <div class="typing-dots">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div class="input-container">
          <input
            type="text"
            class="message-input"
            placeholder={(!this.apikey && !this.token) ? 'API key or token required to chat' : (config.placeholderText || 'Type your message...')}
            value={this.inputValue}
            disabled={!this.apikey && !this.token}
            onInput={(e) => this.handleInputChange(e)}
            onKeyPress={(e) => this.handleKeyPress(e)}
          />
          
          {/* Hidden file input */}
          <input
            type="file"
            id="file-upload-input"
            style={{ display: 'none' }}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
            onChange={(e) => this.handleFileUpload(e)}
          />
          
          {config.enableFileUpload && (
            <button 
              class="feature-btn file-upload"
              onClick={() => this.el.shadowRoot?.getElementById('file-upload-input')?.click()}
              disabled={this.isUploading}
              title="Upload file"
            >
              {this.isUploading ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white" class="spinning">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
                </svg>
              )}
            </button>
          )}
          
          {config.enableSpeech && (
            <button class="feature-btn speech">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
              </svg>
            </button>
          )}
          
          <button class="send-btn" disabled={!this.apikey && !this.token} onClick={() => this.sendMessage()}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </div>

        {/* Tabs */}
        {/*
        <div class="tabs">
          <div class="tab active">
            <span>Chat</span>
          </div>
          <div class="tab">
            <span>Voice</span>
          </div>
          <div class="tab">
            <span>History</span>
          </div>
        </div>{*/}

        {/* Powered By */}
        {config.showPoweredBy && (
          <div class="powered-by">
            {/* <img src={config.poweredByLogo} alt="Powered By Logo" /> */}
            {config.poweredByText} <a href={config.poweredByUrl} target="_blank" rel="noopener noreferrer" style={{color: config.poweredByColor, textDecoration: 'none'}}>{config.poweredByBrand}</a>
          </div>
        )}
          </div>
        )}
      </Host>
    );
  }
}
